import React, { useState, useEffect, useCallback } from 'react';
import { FiUsers, FiPlus, FiTrash2 } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import { getFamilyGroup, saveFamilyGroup } from '../utils/api';

const RELATIONSHIPS = ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'];

const emptyForm = {
  firstName: '', lastName: '', relationship: '', phone: '', email: '', consentGiven: false,
};

export default function FamilyManager() {
  const [members, setMembers] = useState([]);
  const [form, setForm]       = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFamilyGroup();
      setMembers(res.data.members || []);
    } catch (e) {
      console.error('FamilyManager load failed', e);
      setError('Could not load family members.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = () => {
    if (!form.firstName.trim() || !form.relationship) {
      setError('First name and relationship are required.');
      return;
    }
    if (!form.consentGiven) {
      setError('DPDPA consent is required before adding a family member.');
      return;
    }
    setMembers(prev => [...prev, { ...form, status: 'active' }]);
    setForm(emptyForm);
    setError('');
  };

  const handleRemove = (idx) => {
    setMembers(prev => prev.map((m, i) => i === idx ? { ...m, status: 'removed' } : m));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await saveFamilyGroup({ members });
      setSuccess('Family members saved.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const active = members.filter(m => m.status !== 'removed');

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f7f9f7' }}>
      <Sidebar />
      <main style={{ marginLeft: 260, padding: '36px 32px', flex: 1 }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <FiUsers size={22} color="#2e7d32" />
          <h1 style={{ margin: 0, fontSize: 22, fontFamily: "'Raleway',sans-serif",
                       fontWeight: 700, color: '#1a3a1a' }}>
            My Family Members
          </h1>
        </div>

        {error   && <div style={styles.banner('error')}>{error}</div>}
        {success && <div style={styles.banner('success')}>{success}</div>}

        {/* Add member form */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Add a Family Member</h2>
          <div style={styles.formGrid}>
            <input style={styles.input} placeholder="First name *"
              value={form.firstName}
              onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
            <input style={styles.input} placeholder="Last name"
              value={form.lastName}
              onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
            <select style={styles.input} value={form.relationship}
              onChange={e => setForm(f => ({ ...f, relationship: e.target.value }))}>
              <option value="">Relationship *</option>
              {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <input style={styles.input} placeholder="Phone"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            <input style={styles.input} placeholder="Email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          {/* DPDPA 2023: explicit per-member consent before data is stored */}
          <label style={styles.consentLabel}>
            <input type="checkbox" checked={form.consentGiven}
              onChange={e => setForm(f => ({ ...f, consentGiven: e.target.checked }))}
              style={{ marginRight: 8, flexShrink: 0 }} />
            I consent to LifeChiAI processing this family member's health and contact
            information for healing session management, as required under DPDPA 2023.
          </label>
          <button style={styles.addBtn} onClick={handleAdd}>
            <FiPlus style={{ marginRight: 6 }} /> Add Member
          </button>
        </div>

        {/* Members table */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Members ({active.length})</h2>
          {loading ? (
            <div style={{ color: '#888', padding: '16px 0' }}>Loading...</div>
          ) : active.length === 0 ? (
            <div style={{ color: '#888', padding: '16px 0' }}>No family members added yet.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Name', 'Relationship', 'Phone', 'Email', 'Status', ''].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map((m, idx) => m.status === 'removed' ? null : (
                  <tr key={idx} style={styles.tr}>
                    <td style={styles.td}>{m.firstName} {m.lastName}</td>
                    <td style={styles.td}>{m.relationship}</td>
                    <td style={styles.td}>{m.phone || '—'}</td>
                    <td style={styles.td}>{m.email || '—'}</td>
                    <td style={styles.td}>
                      <span style={styles.badge(m.status)}>{m.status || 'active'}</span>
                    </td>
                    <td style={styles.td}>
                      <button style={styles.removeBtn} onClick={() => handleRemove(idx)}
                        title="Remove">
                        <FiTrash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ marginTop: 16 }}>
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

      </main>
    </div>
  );
}

const styles = {
  card:      { background: '#fff', borderRadius: 10, padding: '24px 28px',
               marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  cardTitle: { margin: '0 0 16px', fontSize: 15, fontWeight: 700,
               fontFamily: "'Raleway',sans-serif", color: '#2e7d32' },
  formGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))',
               gap: 12, marginBottom: 14 },
  input:     { padding: '9px 12px', border: '1px solid #ddd', borderRadius: 6,
               fontSize: 13, fontFamily: "'Open Sans',sans-serif", outline: 'none',
               width: '100%', boxSizing: 'border-box' },
  consentLabel: { display: 'flex', alignItems: 'flex-start', fontSize: 12,
                  color: '#555', lineHeight: 1.5, marginBottom: 14, cursor: 'pointer' },
  addBtn:    { display: 'flex', alignItems: 'center', padding: '9px 18px',
               background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6,
               fontSize: 13, fontWeight: 600, cursor: 'pointer',
               fontFamily: "'Raleway',sans-serif" },
  saveBtn:   { padding: '10px 24px', background: '#2e7d32', color: '#fff',
               border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
               cursor: 'pointer', fontFamily: "'Raleway',sans-serif" },
  removeBtn: { background: 'transparent', border: 'none', color: '#e74c3c',
               cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:        { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #f0f0f0',
               fontFamily: "'Raleway',sans-serif", fontWeight: 700,
               fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' },
  tr:        { borderBottom: '1px solid #f7f7f7' },
  td:        { padding: '10px 12px', color: '#333' },
  badge:     (status) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
    fontWeight: 600, background: status === 'active' ? '#e8f5e9' : '#fce4e4',
    color: status === 'active' ? '#2e7d32' : '#c62828',
  }),
  banner:    (type) => ({
    padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13,
    background: type === 'error' ? '#fce4e4' : '#e8f5e9',
    color:      type === 'error' ? '#c62828'  : '#2e7d32',
  }),
};
